import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  HStack,
  Text,
  Badge,
  Button,
  IconButton,
  Spacer,
  useColorModeValue
} from '@chakra-ui/react';
import { Cube, CaretLeft, CaretRight, DownloadSimple } from '@phosphor-icons/react';
import ThreeDViewer from './ThreeDViewer';

const ThreeDViewerModal = ({ 
  isOpen, 
  onClose, 
  modelUrl,
  modelData,
  fileObj, 
  title,
  hasMultiple = false,
  onPrevious,
  onNext
}) => {
  const modalBg = useColorModeValue('bg.surface', 'bg.surface');
  const headerBg = useColorModeValue('bg.accent', 'bg.accent');

  // Support both old fileObj prop and new modelData/modelUrl props
  const currentModel = modelData || fileObj;
  const currentUrl = modelUrl || fileObj?.url;
  
  if ((!currentModel && !currentUrl) || !isOpen) return null;

  const handleDownload = () => {
    const url = currentUrl;
    const name = currentModel?.name || 'model.3d';
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg={modalBg}
        maxW="95vw"
        maxH="95vh"
        m={4}
      >
        <ModalHeader
          bg={headerBg}
          borderTopRadius="md"
          py={3}
        >
          <HStack spacing={3} width="100%">
            <Cube size={24} />
            <Text textStyle="carved" fontSize="lg">
              {title || currentModel?.name || '3D Model Viewer'}
            </Text>
            <Spacer />
            <Badge 
              variant="subtle" 
              bg="ancient.terracotta" 
              color="white"
            >
              {currentModel?.type?.toUpperCase() || currentModel?.modelInfo?.type || '3D'}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody p={4}>
          <ThreeDViewer
            url={currentUrl}
            fileName={currentModel?.name}
            fileExtension={currentModel?.name?.split('.').pop()}
            width="100%"
            height="70vh"
            showControls={true}
            autoRotate={false}
            onDownload={handleDownload}
          />
        </ModalBody>
        
        {(hasMultiple || currentUrl) && (
          <ModalFooter>
            <HStack spacing={3} width="100%">
              {hasMultiple && onPrevious && (
                <IconButton
                  icon={<CaretLeft />}
                  aria-label="Previous model"
                  onClick={onPrevious}
                  variant="outline"
                  size="sm"
                />
              )}
              
              <Spacer />
              
              <Button
                leftIcon={<DownloadSimple />}
                onClick={handleDownload}
                variant="outline"
                size="sm"
                isDisabled={!currentUrl}
              >
                Download
              </Button>
              
              <Spacer />
              
              {hasMultiple && onNext && (
                <IconButton
                  icon={<CaretRight />}
                  aria-label="Next model"
                  onClick={onNext}
                  variant="outline"
                  size="sm"
                />
              )}
            </HStack>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ThreeDViewerModal;
